import { i18n } from "../../internationalize/i18n.service"
import { validateEmail } from "../../core/helpers"
import { AuthService } from "../../auth/services/auth.service"
import type { ProfileService } from "../profile.service"
import { FeedbackService } from "../../newsletters/userMessage.service"

import "./EmailForm.styles.scss"

export class EmailFormComponent extends HTMLElement {
    private authService: AuthService = window.tbwServices.authService
    private profileService: ProfileService = window.tbwServices.profileService
    private feedbackService!: FeedbackService

    private emailInput!: HTMLInputElement
    private formContainer!: HTMLFormElement
    private submitButton!: HTMLButtonElement

    constructor() {
        super()
        this.innerHTML = this.render()

        this.emailInput = this.querySelector(".js-email-input")!
        this.formContainer = this.querySelector(".js-email-form")!
    }

    connectedCallback() {
        if (!this.authService.isAuthenticated) {
            this.showSignInPrompt()
            return
        }

        this.feedbackService = new FeedbackService(this)
        this.init()
    }

    private showSignInPrompt(): void {
        const prompt = document.createElement("div")
        prompt.className = "sign-in-prompt"
        prompt.textContent =
            i18n.getLanguage() === "fr"
                ? "Connectez-vous pour modifier votre adresse email"
                : "Sign in to update your email address"
        this.appendChild(prompt)
    }

    private init(): void {
        if (!this.emailInput || !this.formContainer) return

        this.emailInput.value = this.authService.profile.userEmail
        this.submitButton = this.createSubmitButton()
        this.formContainer.appendChild(this.submitButton)

        this.formContainer.addEventListener("submit", (event) => {
            event.preventDefault()
            this.handleSubmit(this.submitButton)
        })
    }

    private render(): string {
        const placeholder =
            i18n.getLanguage() === "fr"
                ? "Votre adresse mail"
                : "Your email address"

        return `
            <form class="email-form_container js-email-form">
                <input
                    type="email"
                    class="text-input js-email-input"
                    name="email"
                    placeholder="${placeholder}"
                    autocomplete="email"
                />
            </form>
        `
    }

    private createSubmitButton(): HTMLButtonElement {
        const button = document.createElement("button")
        button.className = "submit-button"
        button.type = "submit"
        button.textContent =
            i18n.getLanguage() === "fr" ? "Mettre à jour" : "Update"
        return button
    }

    private async handleSubmit(button: HTMLButtonElement): Promise<void> {
        this.feedbackService.hideMessages()
        this.feedbackService.setSubmitButtonLoading(button, true)

        try {
            if (!validateEmail(this.emailInput.value)) {
                const errorMessage =
                    i18n.getLanguage() === "fr"
                        ? "Veuillez entrer une adresse email valide"
                        : "Please enter a valid email address"
                throw new Error(errorMessage)
            }

            await this.profileService.updateEmail(this.emailInput.value)

            const successMessage =
                i18n.getLanguage() === "fr"
                    ? "Email mis à jour"
                    : "Email updated"
            this.feedbackService.showSuccessMessage(successMessage)

            await this.authService.checkAuth()
            this.feedbackService.reloadPageWithTimeout()
        } catch (error: unknown) {
            const message =
                error instanceof Error
                    ? error.message
                    : i18n.getLanguage() === "fr"
                      ? "Erreur lors de la mise à jour"
                      : "Error updating email"
            this.feedbackService.showErrorMessage(message)
        } finally {
            this.feedbackService.setSubmitButtonLoading(button, false)
        }
    }
}
